import { Component, type ErrorInfo, type ReactNode } from "react";

import { Button } from "@/components/ui/button";

interface AppErrorBoundaryProps {
  children: ReactNode;
}

interface AppErrorBoundaryState {
  error: Error | null;
}

/**
 * Last line of defence for a render-time throw.
 *
 * React unmounts the entire root when a render throws and nothing catches it,
 * which looks to the user exactly like the page reloading: the viewer, the
 * conversation and every bit of in-memory state are gone. One malformed
 * streaming chunk used to be enough. Keeping a boundary here turns that into a
 * message the user can act on, and keeps the failure attributable.
 */
export class AppErrorBoundary extends Component<AppErrorBoundaryProps, AppErrorBoundaryState> {
  state: AppErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): AppErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("Unhandled render error:", error, info.componentStack);
  }

  private readonly handleRetry = () => {
    this.setState({ error: null });
  };

  private readonly handleReload = () => {
    window.location.reload();
  };

  render() {
    const { error } = this.state;
    if (!error) {
      return this.props.children;
    }

    return (
      <div className="grid min-h-screen place-items-center bg-background p-6">
        <div className="flex w-full max-w-lg flex-col gap-4">
          <div className="flex flex-col gap-1">
            <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Unexpected error
            </span>
            <h1 className="text-lg font-semibold">Something went wrong while rendering this page</h1>
            <p className="text-sm text-muted-foreground">
              The error below was not handled. Retrying re-renders the page without reloading, so
              anything still in memory survives.
            </p>
          </div>
          <pre className="max-h-48 overflow-auto rounded-md bg-muted p-3 text-xs whitespace-pre-wrap">
            {error.message || String(error)}
          </pre>
          <div className="flex gap-2">
            <Button onClick={this.handleRetry}>Retry</Button>
            <Button variant="outline" onClick={this.handleReload}>
              Reload page
            </Button>
          </div>
        </div>
      </div>
    );
  }
}
